import { read, write } from './storage';
import API_BASE from './config';

const RESULTS_KEY = 'f1_results';

function mapResultRowsToPositions(rows = []) {
    const ordered = [];
    (rows || []).forEach((r, index) => {
        if (typeof r === 'number') {
            ordered[index] = r;
            return;
        }

        const driverId = r?.driver_id || r?.id || null;
        const position = Number(r?.position);
        if (driverId == null) return;

        if (Number.isInteger(position) && position > 0) {
            ordered[position - 1] = driverId;
        } else {
            ordered[index] = driverId;
        }
    });
    return ordered;
}

export function getResults(raceId) {
    if (!raceId) return Promise.resolve([]);
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/results`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((rows) => {
            return mapResultRowsToPositions(rows);
        })
        .catch((err) => {
            console.error('resultsApi.getResults error', err);
            const obj = read(RESULTS_KEY) || {};
            return Promise.resolve(obj[raceId] || []);
        });
}

export function saveResults(results, raceId, dnfs = [], startPosition = 1) {
    if (!raceId) return Promise.reject(new Error('raceId required'));
    // Try backend first
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ results, dnfs, startPosition }),
    }).then((res) => {
        if (!res.ok) throw new Error('Failed to save results');
        return res.json();
    }).then((data) => {
        // update local cache with driver IDs
        const obj = read(RESULTS_KEY) || {};
        const ids = Array.isArray(data) ? mapResultRowsToPositions(data) : results;
        obj[raceId] = ids;
        write(RESULTS_KEY, obj);
        return Promise.resolve(obj[raceId]);
    }).catch((err) => {
        console.error('resultsApi.saveResults error', err);
        const obj = read(RESULTS_KEY) || {};
        obj[raceId] = results;
        write(RESULTS_KEY, obj);
        return Promise.resolve(results);
    });
}

export function getRacePicks(raceId) {
    if (!raceId) return Promise.resolve([]);
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/picks`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((rows) => rows)
        .catch((err) => {
            console.error('resultsApi.getRacePicks error', err);
            const obj = read(RESULTS_KEY) || {};
            // fallback: if we have local picks stored separately, try to map them
            return Promise.resolve([]);
        });
}

export function getRaceDnfs(raceId) {
    if (!raceId) return Promise.resolve([]);
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/dnfs`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((rows) => rows.map((r) => r.driver_id))
        .catch((err) => {
            console.error('resultsApi.getRaceDnfs error', err);
            return Promise.resolve([]);
        });
}

export function syncFinalRaceResults(raceId) {
    if (!raceId) return Promise.reject(new Error('raceId required'));
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/sync-final-results`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
    })
        .then(async (res) => {
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.error || 'Failed to sync final race results');
            }
            return res.json();
        })
        .then((payload) => {
            const rows = Array.isArray(payload?.rows) ? payload.rows : [];
            const ids = mapResultRowsToPositions(rows);
            const obj = read(RESULTS_KEY) || {};
            obj[raceId] = ids;
            write(RESULTS_KEY, obj);
            return payload;
        });
}

export function syncRaceResultsFromHistorical(raceId, season = 2025) {
    if (!raceId) return Promise.reject(new Error('raceId required'));
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/sync-historical-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ season }),
    })
        .then(async (res) => {
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.error || 'Failed to sync historical race results');
            }
            return res.json();
        })
        .then((payload) => {
            const rows = Array.isArray(payload?.rows) ? payload.rows : [];
            const ids = mapResultRowsToPositions(rows);
            const obj = read(RESULTS_KEY) || {};
            obj[raceId] = ids;
            write(RESULTS_KEY, obj);
            return payload;
        });
}
